import { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../config/env';

const FAQ_CACHE = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const useFAQs = (elementID) => {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFAQs = useCallback(async () => {
    if (!elementID) {
      setFaqs([]);
      setIsLoading(false);
      return;
    }

    // Check cache first
    const cacheKey = `faqs_${elementID}`;
    const cached = FAQ_CACHE.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setFaqs(cached.data);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const backendUrl = getBackendUrl();
      if (!backendUrl) {
        throw new Error('Backend URL is not configured');
      }

      // Fetch from elements-config API
      const response = await fetch(
        `${backendUrl}/elements-config?elementID=${elementID}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch FAQs: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('🔍 [useFAQs Hook] elements-config API response:', data);

      // Extract FAQs from the response - ONLY use form FAQs
      let faqData = [];
      
      // Check for FAQs in the correct field name
      // Note: elements-config API returns 'formFaqs' field for form-specific FAQs
      if (data.formFaqs && Array.isArray(data.formFaqs) && data.formFaqs.length > 0) {
        faqData = data.formFaqs;
      } else {
        // Explicitly empty if no form FAQs - no fallback to merchant FAQs
        faqData = [];
      }

      // Sort FAQs by order if available, otherwise by index
      faqData = faqData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });

      // Cache the result
      FAQ_CACHE.set(cacheKey, {
        data: faqData,
        timestamp: Date.now()
      });

      setFaqs(faqData);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(err.message);
      setFaqs([]);
    } finally {
      setIsLoading(false);
    }
  }, [elementID]);

  useEffect(() => {
    fetchFAQs();
  }, [fetchFAQs]);

  // Function to refresh FAQs (useful for manual refresh)
  const refreshFAQs = useCallback(() => {
    if (elementID) {
      FAQ_CACHE.delete(`faqs_${elementID}`);
      fetchFAQs();
    }
  }, [elementID, fetchFAQs]);

  return {
    faqs,
    isLoading,
    error,
    refreshFAQs
  };
};

export default useFAQs;
